import * as Yup from 'yup'
import React, { useState, useRef } from 'react'
import { Formik, Form, Field, ErrorMessage, useField } from 'formik'
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  Checkbox,
  FormControlLabel
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useSelector } from 'react-redux'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker, TimePicker } from '@mui/x-date-pickers'
import EastIcon from '@mui/icons-material/East'
import dayjs from 'dayjs'
import CustomLocationDialog from './CustomLocation'
import CustomCategoryDialog from './CustomCategory'
import CustomGroupDialog from './CustomGroup'
import InventoryDetailAPIs from '../../../utilities/api/inventory/AdminInventoryDetail'
import InventoryGroupAPIs from '../../../utilities/api/group/AdminInventoryGroupList'
import InventoryLocationAPIs from '../../../utilities/api/location/AdminLocationList'
import InventoryCategoryAPIs from '../../../utilities/api/category/AdminInventoryCategoryList'
import { ImageBaseURL } from '../../../utilities/api/axios'
import { toast } from 'react-toastify'
import FormField from '../../shared/FormField'
import { KeyboardArrowDown } from '@mui/icons-material'

const taglist = [
  'Free',
  'Issue',
  'Purchase',
  'Exhaustible',
  'NonExhaustible',
  'InLabOnly',
  'HighValue'
]
const availabilityList = ['Very Low', 'Low', 'Moderate', 'High', 'Always']
const statusList = ['Operational', 'Under Maintenance', 'Out of Service']

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Product type is required'),
  upc: Yup.number(),
  // stock_available: Yup.number().required('Stock available is required'),
  stock_available: Yup.number()
    .required('Stock available is required')
    .test('is-lte', 'Stock available cannot be more than stock total', function (value) {
      return value <= this.parent.stock_total
    }),
  stock_total: Yup.number().required('Stock total is required'),
  instances: Yup.number().required('Instances is required'),
  group: Yup.string(),
  category: Yup.string(),
  location: Yup.string(),
  availability: Yup.string(),
  status: Yup.string(),
  supervised: Yup.boolean(),
  tags: Yup.array()
    .required('This field is required')
    .test('one-of-three', 'Only one of Free, Issue, Purchase can be selected', (value) => {
      const count = ['Free', 'Issue', 'Purchase'].filter((tag) => value.includes(tag)).length
      return count <= 1
    })
    .test(
      'one-of-required-three',
      'At least one of Free, Issue, Purchase must be selected',
      (value) => {
        return ['Free', 'Issue', 'Purchase'].some((tag) => value.includes(tag))
      }
    )
    .test('exclusivity', 'Exhaustible and NonExhaustible cannot be selected together', (value) => {
      return !(value.includes('Exhaustible') && value.includes('NonExhaustible'))
    }),
  manufacturer: Yup.string(),
  purchase_cost: Yup.string(),
  image: Yup.mixed().nullable(),
  description: Yup.string(),
  stock_unit: Yup.string(),
  purchase_date: Yup.date().nullable(),
  warranty_expiration: Yup.date().nullable()
})

const styledField = {
  maxWidth: '70%',
  width: '100%',
  padding: '0px 4px 8px 6px',
  '& .MuiFormLabel-root': {
    marginLeft: '4px'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderRadius: '12px',
    border: '1px solid #8C8C8C'
  }
}

const headerProps = { sx: { fontWeight: '400', color: '#666666' } }

const EditInventoryDialog = ({ currentRow, open, onClose, update, setUpdate, flag, setFlag }) => {
  const user = useSelector((state) => state.user.user)
  const token = user.access

  const [openCustomLocation, setOpenCustomLocation] = useState(false)
  const [customLocation, setCustomLocation] = useState('')
  const [openCustomCategory, setOpenCustomCategory] = useState(false)
  const [customCategory, setCustomCategory] = useState('')
  const [openCustomGroup, setOpenCustomGroup] = useState(false)
  const [customGroup, setCustomGroup] = useState('')
  const [newImage, setNewImage] = useState(null)

  const category = useSelector((state) => state.category.category_list)
  const location = useSelector((state) => state.location.location_list)
  const group = useSelector((state) => state.group.group_list)

  const categories = category.map((item) => item.name)
  const locations = location.map((item) => item.name)
  const groups = group.map((item) => item.name)

  const initialTags = []
  if (currentRow) {
    if (currentRow.tag_1) initialTags.push(currentRow.tag_1)
    if (currentRow.tag_2) initialTags.push(currentRow.tag_2)
    if (currentRow.tag_3) initialTags.push(currentRow.tag_3)
    if (currentRow.tag_4) initialTags.push(currentRow.tag_4)
  }

  console.log('initialTags', initialTags)

  const handleConfirmLocation = () => {
    setOpenCustomLocation(false)
    InventoryLocationAPIs.LocationListPost(customLocation, token)
      .then((result) => {
        console.log(result)
        setFlag((prev) => !prev)
      })
      .catch((err) => {
        console.log('error', err)
      })
  }

  const handleConfirmCategory = () => {
    setOpenCustomCategory(false)
    InventoryCategoryAPIs.CategoryListPost(customCategory, token)
      .then((result) => {
        console.log(result)
        setFlag((prev) => !prev)
      })
      .catch((err) => {
        console.log('error', err)
      })
  }

  const handleConfirmGroup = () => {
    setOpenCustomGroup(false)
    InventoryGroupAPIs.GroupListPost(customGroup, token)
      .then((result) => {
        console.log(result)
        setFlag((prev) => !prev)
      })
      .catch((err) => {
        console.log('error', err)
      })
  }

  const formatDate = (date) => {
    return date ? dayjs(date).format('YYYY-MM-DD') : ''
  }

  const initialValues = {
    name: currentRow?.name || '',
    upc: currentRow?.upc || '',
    manufacturer: currentRow?.manufacturer || '',
    description: currentRow?.description || '',
    location: currentRow?.location || '',
    stock_available: currentRow?.stock_available || '',
    stock_total: currentRow?.stock_total || '',
    instances: currentRow?.instances || '',
    stock_unit: currentRow?.stock_unit || '',
    purchase_cost: currentRow?.purchase_cost || '',
    purchase_date: formatDate(currentRow?.purchase_date) || '',
    warranty_expiration: formatDate(currentRow?.warranty_expiration) || '',
    image: null,
    category: currentRow?.category || '',
    group: currentRow?.group || '',
    availability: currentRow?.availability || '',
    status: currentRow?.status || '',
    supervised: currentRow?.supervised || false,
    tags: initialTags,
    is_Hidden: false
  }

  const mapTagToIndex = (tag) => {
    const tagMap = {
      Free: '0',
      Issue: '1',
      Purchase: '2',
      Exhaustible: '3',
      NonExhaustible: '4',
      InLabOnly: '5',
      HighValue: '6'
    }
    return tagMap[tag] || ''
  }

  const FileUpload = ({ name, heading, sx, setFieldValue }) => {
    const fileInputRef = useRef(null)

    const handleFileChange = (event) => {
      const file = event.currentTarget.files[0]

      if (file) {
        // Check if file size exceeds 1MB (1MB = 1,048,576 bytes)
        const fileSizeLimit = 1048576 // 1MB in bytes
        if (file.size > fileSizeLimit) {
          toast.error('File size should not exceed 1MB')
          return // Stop the upload process if the file is too large
        }

        // Check if the file is an image
        if (!file.type.startsWith('image/')) {
          toast.error('Only image files are allowed!')
          return
        }

        // If both validations pass, set the image and display a preview
        setFieldValue(name, file)
        setNewImage(URL.createObjectURL(file)) // Set the new image URL
      }
    }

    return (
      <>
        <Typography
          variant="h6"
          sx={{ fontWeight: 'high', marginBottom: 1, color: 'grey', paddingLeft: '5px' }}
        >
          {heading}
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileChange}
          style={{ display: 'none' }}
          accept="image/*" // Restrict file selection to images only
        />
        <Button variant="contained" onClick={() => fileInputRef.current.click()} sx={sx}>
          Upload File
        </Button>
        <ErrorMessage name={name}>
          {(msg) => <FormHelperText error>{msg}</FormHelperText>}
        </ErrorMessage>
      </>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit Inventory</DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          const newTagsSet = new Set(values.tags)
          const updatedTags = Array.from(newTagsSet)

          // Ensure there are exactly 4 tags, filling the remaining with null
          while (updatedTags.length < 4) {
            updatedTags.push('')
          }

          const updatedValues = {
            ...values,
            tag_1: updatedTags[0],
            tag_2: updatedTags[1],
            tag_3: updatedTags[2],
            tag_4: updatedTags[3],
            tag1: mapTagToIndex(updatedTags[0]),
            tag2: mapTagToIndex(updatedTags[1]),
            tag3: mapTagToIndex(updatedTags[2]),
            tag4: mapTagToIndex(updatedTags[3])
          }

          console.log('onSubmit values', updatedValues)

          const formData = new FormData()
          Object.keys(updatedValues).forEach((key) => {
            if (key === 'image' && values.image) {
              formData.append(key, values.image)
            } else if (key !== 'image') {
              formData.append(key, updatedValues[key])
            }
          })

          // Handle availability and availability1
          if (values.availability) {
            // Map the selected availability to its index
            const availabilityIndex = availabilityList.indexOf(values.availability)
            formData.append('availability', values.availability)
            formData.append('availability1', availabilityIndex.toString())
          } else {
            // Send empty strings for availability and availability1 if not set
            formData.append('availability', '')
            formData.append('availability1', '')
          }

          // Handle status and status1
          if (values.status) {
            const statusIndex = statusList.indexOf(values.status)
            formData.append('status', values.status)
            formData.append('status1', statusIndex.toString())
          } else {
            formData.append('status', '')
            formData.append('status1', '')
          }

          // Handle supervised
          formData.append('supervised', values.supervised)

          InventoryDetailAPIs.InventoryDetailPatch(currentRow.id, formData, token)
            .then((result) => {
              console.log('InventoryDetailPatch result', result)
              onClose()
              setUpdate((prev) => !prev)
            })
            .catch((error) => {
              console.log(error)
              alert(`${error.response.data}`)
            })
        }}
      >
        {({ handleSubmit, values, touched, errors, handleChange, setFieldValue }) => (
          <Form onSubmit={handleSubmit}>
            <DialogContent>
              <Grid container spacing={10}>
                <Grid item xs={12} sm={6}>
                  <FormField
                    name="name"
                    label="Name"
                    heading="Name*"
                    sx={styledField}
                    headerProps={headerProps}
                  />
                  <FormField
                    name="upc"
                    label="Enter upc"
                    fieldType="textfield"
                    heading="Upc"
                    sx={styledField}
                    headerProps={headerProps}
                  />
                  <FormField
                    name="stock_available"
                    label=""
                    fieldType="textfield"
                    heading="Stock Available*"
                    sx={styledField}
                    headerProps={headerProps}
                  />
                  <FormField
                    name="stock_total"
                    label=""
                    fieldType="textfield"
                    heading="Stock Total*"
                    sx={styledField}
                    headerProps={headerProps}
                  />
                  <FormField
                    name="instances"
                    label="Enter Instances"
                    fieldType="textfield"
                    heading="Instances*"
                    sx={styledField}
                    headerProps={headerProps}
                  />
                  <FormField
                    name="manufacturer"
                    label="Enter Manufacturer Name"
                    fieldType="textfield"
                    heading="Manufacturer"
                    sx={styledField}
                    headerProps={headerProps}
                  />
                  <Box>
                    <FormField
                      name="location"
                      label="Select location"
                      fieldType="dropdown"
                      heading="Location"
                      options={locations}
                      sx={styledField}
                      SelectProps={{
                        IconComponent: KeyboardArrowDown
                      }}
                      addOption
                      handleAddOption={() => setOpenCustomLocation(true)}
                      headerProps={headerProps}
                    />
                    <IconButton
                      aria-label="custom-location"
                      onClick={() => setOpenCustomLocation(true)}
                    >
                      <AddIcon sx={{ color: 'green' }} />
                    </IconButton>
                  </Box>
                  <CustomLocationDialog
                    openCustomLocation={openCustomLocation}
                    setCustomLocation={setCustomLocation}
                    onClose={() => setOpenCustomLocation(false)}
                    onConfirm={() => {
                      setFieldValue('location', customLocation)
                      handleConfirmLocation()
                    }}
                  />
                  <FormField
                    name="purchase_cost"
                    label="Enter Purchase Cost"
                    fieldType="textfield"
                    heading="Purchase Cost"
                    sx={styledField}
                    headerProps={headerProps}
                  />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: '400', padding: '5px', color: '#666666' }}
                  >
                    Inventory Image
                  </Typography>
                  {newImage ? (
                    <img
                      src={newImage}
                      alt="New Upload"
                      style={{ width: '200px', height: '200px' }}
                    />
                  ) : currentRow.image ? (
                    <img
                      src={`${ImageBaseURL}${currentRow.image}`}
                      alt={currentRow.image.split('/').pop()}
                      style={{ width: '200px', height: '200px' }}
                    />
                  ) : null}
                  <FileUpload
                    name="image"
                    // heading="Inventory Image"
                    sx={{ ...styledField, padding: '6px 8px' }}
                    setFieldValue={setFieldValue}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <FormField
                      name="category"
                      label="Select Category"
                      fieldType="dropdown"
                      heading="Category"
                      options={categories}
                      sx={styledField}
                      SelectProps={{
                        IconComponent: KeyboardArrowDown
                      }}
                      addOption
                      handleAddOption={() => setOpenCustomCategory(true)}
                      headerProps={headerProps}
                    />
                  </Box>
                  <CustomCategoryDialog
                    openCustomCategory={openCustomCategory}
                    setCustomCategory={setCustomCategory}
                    onClose={() => setOpenCustomCategory(false)}
                    onConfirm={() => {
                      setFieldValue('category', customCategory)
                      handleConfirmCategory()
                    }}
                  />
                  <FormField
                    name="tags"
                    fieldType="dropdown"
                    heading="Tags*"
                    label="Select Status"
                    options={taglist}
                    sx={styledField}
                    SelectProps={{
                      IconComponent: KeyboardArrowDown,
                      multiple: true
                    }}
                    headerProps={headerProps}
                  />
                  <Box display="flex" flexDirection="column" position="relative">
                    <FormField
                      name="group"
                      fieldType="dropdown"
                      heading="Group"
                      label="Select Group"
                      options={groups}
                      sx={styledField}
                      SelectProps={{
                        IconComponent: KeyboardArrowDown
                      }}
                      addOption
                      handleAddOption={() => setOpenCustomGroup(true)}
                      headerProps={headerProps}
                    />
                  </Box>
                  <CustomGroupDialog
                    openCustomGroup={openCustomGroup}
                    setCustomGroup={setCustomGroup}
                    onClose={() => setOpenCustomGroup(false)}
                    onConfirm={() => {
                      setFieldValue('group', customGroup)
                      handleConfirmGroup()
                    }}
                  />
                  <FormField
                    name="stock_unit"
                    label="Enter stock unit"
                    fieldType="textfield"
                    heading="Stock Unit"
                    sx={styledField}
                    headerProps={headerProps}
                  />
                  <FormField
                    name="purchase_date"
                    label="date"
                    fieldType="date"
                    heading="Purchase Date"
                    format="YYYY-MM-DD"
                    sx={styledField}
                    headerProps={headerProps}
                  />
                  <FormField
                    name="warranty_expiration"
                    label="date"
                    fieldType="date"
                    heading="Warranty Expiration"
                    format="YYYY-MM-DD"
                    sx={styledField}
                    headerProps={headerProps}
                  />
                  <FormField
                    name="availability"
                    label="Select availability"
                    fieldType="dropdown"
                    heading="Availability"
                    options={availabilityList}
                    sx={styledField}
                    headerProps={headerProps}
                  />
                  <FormField
                    name="status"
                    label="Select status"
                    fieldType="dropdown"
                    heading="Status"
                    options={statusList}
                    sx={styledField}
                    SelectProps={{
                      IconComponent: KeyboardArrowDown
                    }}
                    headerProps={headerProps}
                  />
                  <Box sx={{ padding: '5px' }}>
                    <FormControlLabel
                      control={
                        <Field
                          as={Checkbox}
                          name="supervised"
                          color="primary"
                          checked={values.supervised}
                          onChange={handleChange}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: '1.25rem', color: '#666666' }}>
                          Supervised
                        </Typography>
                      }
                      labelPlacement="end"
                    />
                    {touched.supervised && errors.supervised && (
                      <FormHelperText error>{errors.supervised}</FormHelperText>
                    )}
                  </Box>
                </Grid>
              </Grid>
              <Typography
                variant="h6"
                sx={{ fontWeight: '400', fontSize: '1.2rem', color: '#666666', mt: 4 }}
              >
                Description
              </Typography>
              <Field
                as={TextField}
                name="description"
                label="Enter Description"
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                value={values.description}
                onChange={handleChange}
                sx={{ mt: 2, mb: 1 }}
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={onClose}
                color="primary"
                variant="outlined"
                sx={{
                  textTransform: 'none',
                  fontWeight: 'bold',
                  borderRadius: '12px',
                  borderWidth: '1px',
                  borderColor: 'action.active',
                  '&:hover': {
                    borderWidth: '1px',
                    borderColor: 'action.active'
                  },
                  mr: 1
                }}
              >
                Discard
              </Button>
              <Button
                type="submit"
                variant="contained"
                disableElevation
                sx={{
                  textTransform: 'none',
                  fontWeight: 'bold',
                  color: 'common.white',
                  backgroundColor: 'primary.main',
                  borderRadius: '12px',
                  '&:hover': {
                    backgroundColor: 'primary.dark'
                  }
                }}
              >
                Save Changes
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

export default EditInventoryDialog
